import { ChevronRight } from 'lucide-react';
import { MonoLabel } from '@/components/editorial';
import { timeAgo } from '@/lib/utils';
import { conversationTitle } from '@/lib/conversation-filter';
import type { ConversationSummary } from '@/types/chat';

/**
 * One line per thread (ui-ux-guideline §3.1): title, relative time, chevron.
 *
 * Victor, 2026-09-21: "Apply aggressively the subtract for clarity
 * guidelines … 1) Single line". What left, and why:
 *
 *   - The 32px indigo message-bubble avatar. It was identical on every row,
 *     so it measured nothing — §1.7 removes such a mark rather than
 *     relabelling it. Losing it is also what makes one line possible.
 *   - The stacked title-over-timestamp block. The time moves to the right
 *     edge of the same line, where a scanning reader compares rows against
 *     each other instead of reading each row twice.
 *
 * `timeAgo` is the shared helper in `lib/utils`, not the date-fns variant
 * that lived in `lib/family-activity` — that module retires with the page
 * this row replaces, and two spellings of "how long ago" is one too many.
 *
 * Shared by `ConversationHistoryDialog` and `ConversationArchive` so the
 * quick reach and the full archive cannot drift into two different rows.
 */
export interface ConversationRowProps {
  conversation: ConversationSummary;
  onOpen: (id: string) => void;
}

export function ConversationRow({ conversation, onOpen }: ConversationRowProps) {
  return (
    <button
      type="button"
      onClick={() => onOpen(conversation.id)}
      className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-muted/40 transition-colors"
    >
      <span className="flex-1 min-w-0 truncate text-body text-foreground">
        {conversationTitle(conversation)}
      </span>
      <MonoLabel className="flex-shrink-0">
        {timeAgo(conversation.lastActivity || conversation.updated_at || conversation.created_at)}
      </MonoLabel>
      <ChevronRight className="h-4 w-4 text-stevens-ink-3 flex-shrink-0" aria-hidden="true" />
    </button>
  );
}
